/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, computed, signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { CommentService } from '../services/comment.service';
import { ConfirmActionDialogComponent } from '../common/community/feed/actions/confirm-action-dialog.component';
import { pageWindow } from '../shared/page-window';

type DraftKind = 'new' | 'reply' | 'quote' | 'edit';

interface Draft {
  kind: DraftKind;
  content: string;
  rating?: number;
  // The comment being edited, or replied to.
  target?: any;
  // For replies: the comment's position on the page, so a reply to the last one opens the next page.
  index?: number;
  // For edits: the page the edit started on, so saving or cancelling returns there.
  editPage?: number;
}

const TOPIC_SORTS = { oldest: 'Oldest first', newest: 'Newest first' };
const OTHER_SORTS = { '-threaded': 'Newest first', threaded: 'Oldest first', liked_threaded: 'Popular first' };
// The quote prefix on its own isn't a comment.
const EMPTY_COMMENTS = ['> \n\n', '>'];
const MAX_INDENT = 4;

// The 2026 comment thread (FORUMS_ARTICLES_REDESIGN.html), for the new topic, article, status and tool pages.
// Same behaviour as ut-comment-section, which stays as it is for the legacy pages:
// - topics page through :page and sort oldest first; everything else uses ?comments and threaded sorts;
// - #comment_ID scrolls to the comment (and now focuses it);
// - on topics, a first comment that repeats the original post is left out;
// - reply (not on topics), quote (topics only), edit, delete, like, hide and report as before;
// - no composer on a locked item; logged-out visitors get a sign-in link.
// The composer sits under the thread instead of in a slide-up, and changes are announced.
@Component({
  selector: 'ut-comment-thread',
  templateUrl: './comment-thread.component.html',
  styleUrls: ['./comment-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommentThreadComponent implements OnInit {
  // The commentable's AngularJS service (getPage for topics, commentsPage otherwise).
  @Input({ required: true }) service: any;
  @Input({ required: true }) commentable: any;
  // The signed-in user, or null.
  @Input() user: any;
  // The tool page calls it "Rate and comment".
  @Input() composeLabel = 'Add a comment';
  // Bound by the topic page so locking it hides the composer straight away: the thread is OnPush, and the lock
  // only mutates `commentable`.
  @Input() locked = false;
  // The reply count once a page has loaded, so the page around the thread can show the same number.
  @Output() totalChange = new EventEmitter<number>();

  comments = signal<any[]>([]);
  loading = signal(true);
  failed = signal(false);
  page = signal(1);
  totalPages = signal(1);
  totalItems = signal(0);
  // Older topics store their opening post as the first comment. The API counts it, the thread hides it.
  private openingPostCounted = signal(false);
  replyCount = computed(() => Math.max(this.totalItems() - (this.openingPostCounted() ? 1 : 0), 0));
  sort = signal('');
  draft = signal<Draft>({ kind: 'new', content: '' });
  posting = signal(false);
  announcement = signal('');

  isTopic = false;
  sortOptions: Record<string, string> = {};
  selectedId = '';
  loginUrl = '';

  pages = computed(() => pageWindow(this.page(), this.totalPages()));

  private perPage = 10;
  private announcePage = false;
  // The comment just edited, saved or cancelled, so the page can return to it.
  private editedId: number;
  // Answers to earlier pages are dropped when pages or sorts change quickly.
  private request = 0;
  private indentations: Record<number, number> = {};

  constructor(
    private commentService: CommentService,
    private dialogService: DialogService,
    private sanitizer: DomSanitizer,
    private el: ElementRef<HTMLElement>,
    @Inject('$location') private $location: any,
    @Inject('$filter') private $filter: any,
    @Inject('$state') private $state: any,
    @Inject('$stateParams') private $stateParams: any,
    @Inject('PaginatorService') private PaginatorService: any,
    @Inject('stateStore') private stateStore: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('ServerMessages') private ServerMessages: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
  ) {}

  ngOnInit(): void {
    this.isTopic = this.commentable.type === 'topic';
    this.sortOptions = this.isTopic ? TOPIC_SORTS : OTHER_SORTS;
    this.sort.set(this.$location.search().sort || (this.isTopic ? 'oldest' : '-threaded'));
    this.selectedId = this.$location.hash().toString().split('_')[1] || '';
    this.loginUrl = `/login?return_to=${encodeURIComponent(this.$location.absUrl())}`;
    const requested = this.isTopic ? this.$stateParams.page : parseInt(this.$location.search().comments);
    this.loadPage(Number(requested) || 1, 'initial');
  }

  get sortKeys(): string[] {
    return Object.keys(this.sortOptions);
  }

  get canComment(): boolean {
    return !!this.commentable.current_user_permissions?.comment && !this.locked && !this.commentable.locked;
  }

  get rated(): boolean {
    return this.commentable.type === 'tool';
  }

  indent(comment: any): number {
    return Math.min(comment.indentation || 0, MAX_INDENT);
  }

  // `focus` says what to do after the page lands: scroll to the #comment_ID target, or move to the thread heading.
  loadPage(page: number, focus: 'initial' | 'heading' | 'edited' | 'none' = 'heading', forceUrlChange = false): void {
    this.loading.set(true);
    this.failed.set(false);
    const request = ++this.request;
    const id = this.commentable.slug || this.commentable.id;
    const response = this.isTopic
      ? this.service.getPage(id, page, this.sort(), '')
      : this.service.commentsPage(id, page, this.sort());
    response.then((res) => {
      if (request !== this.request) return;
      const pagination = this.PaginatorService.makePaginationData(res.headers);
      this.perPage = pagination.perPage || this.perPage;
      this.page.set(pagination.currentPage || page);
      this.totalPages.set(pagination.totalPages || 1);
      this.totalItems.set(pagination.totalItems || 0);
      const kept = res.data.filter((c) => !this.repeatsOriginalPost(c));
      if (kept.length < res.data.length) this.openingPostCounted.set(true);
      if (this.commentable.comments) this.commentable.comments.count = this.replyCount();
      this.totalChange.emit(this.replyCount());
      this.comments.set(kept
        .map((c) => ({
          ...this.commentService.addIndentation(c, this.indentations),
          html: this.render(c.content),
        })));
      this.loading.set(false);
      this.updateUrl(page, forceUrlChange);
      this.afterLoad(focus);
    }, (res) => {
      if (request !== this.request) return;
      this.loading.set(false);
      this.failed.set(true);
      this.AlertService.addDanger(res?.statusText);
    });
  }

  goToPage(page: number): void {
    this.announcePage = true;
    this.loadPage(page);
  }

  setSort(sort: string): void {
    this.sort.set(sort);
    // Focus stays on the select, so arrowing through the options isn't interrupted; the reload is announced.
    this.announcePage = true;
    this.loadPage(1, 'none', true);
  }

  startReply(comment: any, index: number): void {
    if (this.LightTesterErrorService.show()) return;
    this.openDraft({
      kind: 'reply', content: `@${comment.user.slug} `, target: comment, index,
    });
  }

  startQuote(comment: any): void {
    if (this.LightTesterErrorService.show()) return;
    this.openDraft({ kind: 'quote', content: `> @${comment.user.slug} wrote: \n> ${comment.content.split('\n').join('\n> ')}\n\n` });
  }

  startEdit(comment: any): void {
    if (this.LightTesterErrorService.show()) return;
    this.openDraft({
      kind: 'edit', content: comment.content, rating: comment.rating, target: comment, editPage: this.page(),
    });
  }

  cancelDraft(): void {
    this.draft.set({ kind: 'new', content: '' });
  }

  // The Cancel button leaves the page when the draft clears, so focus goes to the composer instead of the body.
  // Cancelling an edit returns to the comment, as the editor sits below the list.
  cancelAndFocus(): void {
    const draft = this.draft();
    this.cancelDraft();
    if (draft.kind === 'edit') {
      this.editedId = draft.target.id;
      // A page still loading will land over the edited one, so reload it too (this also drops that request).
      if (draft.editPage && (draft.editPage !== this.page() || this.loading())) {
        this.loadPage(draft.editPage, 'edited');
        return;
      }
      this.afterLoad('edited');
      return;
    }
    this.el.nativeElement.querySelector<HTMLElement>('#threadComposerEditor')?.focus();
  }

  setContent(content: string): void {
    this.draft.update((d) => ({ ...d, content }));
  }

  setRating(rating: number): void {
    this.draft.update((d) => ({ ...d, rating }));
  }

  submit(): void {
    if (this.posting() || this.LightTesterErrorService.show()) return;
    const draft = this.draft();
    const text = draft.content.trim();
    if (!text || EMPTY_COMMENTS.includes(text)) {
      this.say('Can\'t post an empty comment.');
      this.AlertService.addDanger('Can\'t post empty comment!');
      return;
    }
    this.posting.set(true);
    const body: any = { content: draft.content };
    if (draft.rating !== undefined) body.rating = draft.rating;

    if (draft.kind === 'edit') {
      // The whole comment goes back, as ut-comment-section sent it, minus the rendered HTML.
      const comment = { ...draft.target, ...body };
      delete comment.html;
      this.commentService.editComment(comment.id, comment).then(() => {
        this.AlertService.add('Comment successfully changed');
        this.say('Comment changed');
        this.editedId = comment.id;
        this.cancelDraft();
        this.loadPage(draft.editPage || this.page(), 'edited');
      }, (response) => this.AlertService.addDanger(this.ServerMessages.prettyMessage(response)))
        .finally(() => this.posting.set(false));
      return;
    }

    if (draft.kind === 'reply') body.parent_id = draft.target.id;
    this.commentService.postComment(this.commentable.type, this.commentable.id, body).then((res) => {
      this.AlertService.add('Comment successfully posted');
      this.say('Comment posted');
      this.cancelDraft();
      // Where the new comment lands, as ut-comment-section works it out.
      if (!res.data.parent_id) {
        if (['newest', '-threaded'].includes(this.sort())) {
          this.loadPage(1);
        } else {
          this.loadPage(Math.ceil((this.totalItems() + 1) / this.perPage));
        }
      } else if (draft.index === this.perPage - 1) {
        this.loadPage(this.page() + 1);
      } else {
        this.loadPage(this.page(), 'none');
      }
    }, (response) => this.AlertService.addDanger(this.ServerMessages.prettyMessage(response)))
      .finally(() => this.posting.set(false));
  }

  confirmDelete(comment: any): void {
    this.dialogService.open(ConfirmActionDialogComponent, {
      data: {
        title: 'Delete this comment?', text: 'This can\'t be undone.', confirmLabel: 'Delete', destructive: true,
      },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.commentService.deleteComment(comment.id).then(() => {
        this.AlertService.add('Comment successfully deleted');
        this.say('Comment deleted');
        const lastOnPage = this.comments().length <= 1 && this.page() > 1;
        this.loadPage(lastOnPage ? this.page() - 1 : this.page(), 'heading');
      }, () => this.AlertService.addDanger('There was an error'));
    });
  }

  private openDraft(draft: Draft): void {
    this.draft.set(draft);
    setTimeout(() => {
      const composer = this.el.nativeElement.querySelector<HTMLElement>('.thread__composer');
      composer?.scrollIntoView({ block: 'center' });
      // ut-editor puts the id on CodeMirror's input, where people type.
      this.el.nativeElement.querySelector<HTMLElement>('#threadComposerEditor')?.focus();
    });
  }

  // Topics often come back with the opening post as their first comment.
  private repeatsOriginalPost(comment: any): boolean {
    return this.isTopic && this.commentable.content === comment.content && this.commentable.user?.id === comment.user?.id;
  }

  private render(content: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.$filter('uMarkdown')(content));
  }

  // Like ut-comment-section, the URL only changes when the server moved the page or the sort changed.
  private updateUrl(requested: number, force: boolean): void {
    if (this.page() === requested && !force) return;
    const target = this.isTopic
      ? { state: 'singlediscussionpage', params: { discussionId: this.commentable.id } }
      : this.stateStore.getState(this.commentable);
    const params = { ...target.params, [this.isTopic ? 'page' : 'comments']: this.page(), sort: this.sort() };
    this.$state.go(target.state, params, { notify: false, location: 'replace' });
  }

  private afterLoad(focus: 'initial' | 'heading' | 'edited' | 'none'): void {
    if (this.announcePage) {
      this.say(`${this.isTopic ? 'Replies' : 'Comments'}, page ${this.page()} of ${this.totalPages()}`);
      this.announcePage = false;
    }
    if (focus === 'none') return;
    setTimeout(() => {
      const commentId = focus === 'initial' ? this.selectedId : this.editedId;
      const target = focus === 'initial' || focus === 'edited'
        ? commentId && this.el.nativeElement.querySelector<HTMLElement>(`#comment_${commentId}`)
        : this.el.nativeElement.querySelector<HTMLElement>('.thread__title');
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: focus === 'heading' ? 'start' : 'center' });
      target.focus({ preventScroll: true });
    });
  }

  private say(text: string): void {
    this.announcement.set(text);
  }
}
